import { inject } from '@angular/core';
import { LoadForecastSuccessPayload } from '@library/store/homes/homes.interface';
import { Measure } from '@library/store/measures/measures.interface';
import { Module } from '@library/store/modules/modules.interface';
import { NgrxHttpClient } from '@library/utils/services/http-service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createActionGroup, createReducer, on, props } from '@ngrx/store';
import { WeathermapService } from '@store/weathermap.service';
import moment from 'moment-timezone';
import { exhaustMap, filter, map } from 'rxjs';

export enum RainScale {
  RAIN_60MIN = 'rain_60min',
  RAIN_24H = 'rain_24h',
  RAIN_LIVE = 'rain_live',
}

export enum TemperatureQuality {
  LOW = 'low',
  HIGH = 'high',
}

export enum WindType {
  WIND = 'wind',
  GUST = 'gust',
}

const initialState: WeathermapState = {
  selectedStation: null,
  selectedPinType: null,
  temperatureQuality: TemperatureQuality.LOW,
  rainScale: RainScale.RAIN_60MIN,
  windType: WindType.WIND,
  // Todo: use enums
  unitSystem: 0,
  windUnit: 0,
  pressureUnit: 0,
};

interface WeathermapState {
  selectedStation: Station | null;
  selectedPinType: PinType | null;
  temperatureQuality: TemperatureQuality;
  rainScale: RainScale;
  windType: WindType;
  unitSystem: number;
  windUnit: number;
  pressureUnit: number;
}

export type Forecast = LoadForecastSuccessPayload;

export interface Station {
  id: string;
  modules: Module[];
  city: string;
  altitude: number;
  Pressure?: Measure[];
  Temperature?: Measure[];
  Humidity?: Measure[];
  WindStrength?: Measure[];
  WindAngle?: Measure[];
  GustStrength?: Measure[];
  GustAngle?: Measure[];
  Rain?: Measure[];
  forecast?: Forecast;
}

interface GetMeasureResponse {
  body: Record<number, number[]>;
}

interface SimplifiedFutureMeasureResponse {
  body: LoadForecastSuccessPayload;
}

interface GetPublicMeasureResponse {
  body: [
    {
      place: {
        altitude: number;
        location: number[];
        city: string;
      };
      measures: Record<string, unknown>;
    },
  ];
}

export enum PinType {
  TEMPERATURE = 'temperature',
  WIND = 'wind',
  RAIN = 'rain',
}

export const WeathermapActions = createActionGroup({
  source: 'Weathermap Actions',
  events: {
    'Select station': props<{ station: Station }>(),
    'Load pressure success': props<{ payload: GetMeasureResponse['body'] }>(),
    'Load temperature success': props<{ payload: GetMeasureResponse['body'] }>(),
    'Load humidity success': props<{ payload: GetMeasureResponse['body'] }>(),
    'Load wind success': props<{ payload: GetMeasureResponse['body'] }>(),
    'Load rain success': props<{ payload: GetMeasureResponse['body'] }>(),
    'Load forecast success': props<{ payload: SimplifiedFutureMeasureResponse['body'] }>(),
    'Select pin type': props<{ pinType: PinType }>(),
    'Select temperature quality': props<{ temperatureQuality: TemperatureQuality }>(),
    'Select rain scale': props<{ rainScale: RainScale }>(),
    'Select wind type': props<{ windType: WindType }>(),
    'Select unit system': props<{ unitSystem: number }>(),
    'Select unit wind': props<{ windUnit: number }>(),
    'Select unit pressure': props<{ pressureUnit: number }>(),
    'Load station': props<{ stationId: string }>(),
    'Load station success': props<{ payload: Station & { location: number[] } }>(),
    'Associate favorite station': props<{ stationId: string; name: string }>(),
    'Associate favorite station success': props<{ stationId: string }>(),
    'Dissociate favorite station': props<{ stationId: string }>(),
    'Dissociate favorite station success': props<{ stationId: string }>(),
  },
});

export type LoadStationSuccessAction = ReturnType<typeof WeathermapActions.loadStationSuccess>;
export type AssociateFavoriteStationAction = ReturnType<typeof WeathermapActions.associateFavoriteStation>;
export type DissociateFavoriteStationAction = ReturnType<typeof WeathermapActions.dissociateFavoriteStation>;

export interface AppState {
  weathermap: WeathermapState;
}

export const selectWeathermap = (state: AppState): WeathermapState => state.weathermap;

export const WeathermapReducers = createReducer(
  initialState,
  on(WeathermapActions.selectStation, (state, { station }) => ({ ...state, selectedStation: station })),
  on(WeathermapActions.loadPressureSuccess, (state, { payload }) => {
    if (state.selectedStation === null) {
      return state;
    }

    const Pressure = Object.keys(payload)
      .sort()
      .map((key) => ({ x: +key, y: payload[key][0] }));
    return {
      ...state,
      selectedStation: {
        ...state.selectedStation,
        Pressure,
      },
    };
  }),
  on(WeathermapActions.loadTemperatureSuccess, (state, { payload }) => {
    if (state.selectedStation === null) {
      return state;
    }

    const Temperature = Object.keys(payload)
      .sort()
      .map((key) => ({ x: +key, y: payload[key][0] }));
    return {
      ...state,
      selectedStation: {
        ...state.selectedStation,
        Temperature,
      },
    };
  }),
  on(WeathermapActions.loadHumiditySuccess, (state, { payload }) => {
    if (state.selectedStation === null) {
      return state;
    }

    const Humidity = Object.keys(payload)
      .sort()
      .map((key) => ({ x: +key, y: payload[key][0] }));
    return {
      ...state,
      selectedStation: {
        ...state.selectedStation,
        Humidity,
      },
    };
  }),
  on(WeathermapActions.loadWindSuccess, (state, { payload }) => {
    if (state.selectedStation === null) {
      return state;
    }

    const WindStrength = Object.keys(payload)
      .sort()
      .map((key) => ({ x: +key, y: payload[key][0] }));
    const WindAngle = Object.keys(payload)
      .sort()
      .map((key) => ({ x: +key, y: payload[key][1] }));
    const GustStrength = Object.keys(payload)
      .sort()
      .map((key) => ({ x: +key, y: payload[key][2] }));
    const GustAngle = Object.keys(payload)
      .sort()
      .map((key) => ({ x: +key, y: payload[key][3] }));
    return {
      ...state,
      selectedStation: {
        ...state.selectedStation,
        WindStrength,
        WindAngle,
        GustStrength,
        GustAngle,
      },
    };
  }),
  on(WeathermapActions.loadRainSuccess, (state, { payload }) => {
    if (state.selectedStation === null) {
      return state;
    }

    const Rain = Object.keys(payload)
      .sort()
      .map((key) => ({ x: +key, y: payload[key][0] }));
    return {
      ...state,
      selectedStation: {
        ...state.selectedStation,
        Rain,
      },
    };
  }),
  on(WeathermapActions.loadForecastSuccess, (state, { payload }) => {
    if (state.selectedStation === null) {
      return state;
    }

    return {
      ...state,
      selectedStation: {
        ...state.selectedStation,
        forecast: payload,
      },
    };
  }),
  on(WeathermapActions.selectPinType, (state, { pinType }) => ({ ...state, selectedPinType: pinType })),
  on(WeathermapActions.selectTemperatureQuality, (state, { temperatureQuality }) => ({ ...state, temperatureQuality })),
  on(WeathermapActions.selectRainScale, (state, { rainScale }) => ({ ...state, rainScale })),
  on(WeathermapActions.selectWindType, (state, { windType }) => ({ ...state, windType })),
  on(WeathermapActions.selectUnitSystem, (state, { unitSystem }) => ({ ...state, unitSystem })),
  on(WeathermapActions.selectUnitWind, (state, { windUnit }) => ({ ...state, windUnit })),
  on(WeathermapActions.selectUnitPressure, (state, { pressureUnit }) => ({ ...state, pressureUnit })),
);

export const loadPressure$ = createEffect(
  (actions$ = inject(Actions), weathermapService = inject(WeathermapService)) => {
    return actions$.pipe(
      ofType(WeathermapActions.selectStation),
      filter(({ station }) => station !== null),
      exhaustMap(({ station }) => {
        const params = {
          device_id: station.id,
          module_id: station.id,
          scale: 'max',
          type: 'Pressure',
          optimize: false,
          date_begin: moment().subtract(1, 'day').unix().toString(),
          date_end: moment().unix().toString(),
        };
        return weathermapService.post<GetMeasureResponse>('getmeasure', params).pipe(
          map((response) => response.body),
          map((payload) => WeathermapActions.loadPressureSuccess({ payload })),
        );
      }),
    );
  },
  { functional: true },
);

export const loadTemperature$ = createEffect(
  (actions$ = inject(Actions), weathermapService = inject(WeathermapService)) => {
    return actions$.pipe(
      ofType(WeathermapActions.selectStation),
      filter(({ station }) => station?.modules.some((module) => module.type === 'NAModule1')),
      exhaustMap(({ station }) => {
        const params = {
          device_id: station.id,
          module_id: station.modules.find((module) => module.type === 'NAModule1')?.id,
          scale: 'max',
          type: 'Temperature',
          optimize: false,
          date_begin: moment().subtract(1, 'day').unix().toString(),
          date_end: moment().unix().toString(),
        };
        return weathermapService.post<GetMeasureResponse>('getmeasure', params).pipe(
          map((response) => response.body),
          map((payload) => WeathermapActions.loadTemperatureSuccess({ payload })),
        );
      }),
    );
  },
  { functional: true },
);

export const loadHumidity$ = createEffect(
  (actions$ = inject(Actions), weathermapService = inject(WeathermapService)) => {
    return actions$.pipe(
      ofType(WeathermapActions.selectStation),
      filter(({ station }) => station?.modules.some((module) => module.type === 'NAModule1')),
      exhaustMap(({ station }) => {
        const params = {
          device_id: station.id,
          module_id: station.modules.find((module) => module.type === 'NAModule1')?.id,
          scale: 'max',
          type: 'Humidity',
          optimize: false,
          date_begin: moment().subtract(1, 'day').unix().toString(),
          date_end: moment().unix().toString(),
        };
        return weathermapService.post<GetMeasureResponse>('getmeasure', params).pipe(
          map((response) => response.body),
          map((payload) => WeathermapActions.loadHumiditySuccess({ payload })),
        );
      }),
    );
  },
  { functional: true },
);

export const loadWind$ = createEffect(
  (actions$ = inject(Actions), weathermapService = inject(WeathermapService)) => {
    return actions$.pipe(
      ofType(WeathermapActions.selectStation),
      filter(({ station }) => station?.modules.some((module) => module.type === 'NAModule2')),
      exhaustMap(({ station }) => {
        const params = {
          device_id: station.id,
          module_id: station.modules.find((module) => module.type === 'NAModule2').id,
          scale: 'max',
          type: 'WindStrength,WindAngle,GustStrength,GustAngle',
          optimize: false,
          date_begin: moment().subtract(1, 'day').unix().toString(),
          date_end: moment().unix().toString(),
        };
        return weathermapService.post<GetMeasureResponse>('getmeasure', params).pipe(
          map((response) => response.body),
          map((payload) => WeathermapActions.loadWindSuccess({ payload })),
        );
      }),
    );
  },
  { functional: true },
);

export const loadRain$ = createEffect(
  (actions$ = inject(Actions), weathermapService = inject(WeathermapService)) => {
    return actions$.pipe(
      ofType(WeathermapActions.selectStation),
      filter(({ station }) => station?.modules.some((module) => module.type === 'NAModule3')),
      exhaustMap(({ station }) => {
        const params = {
          device_id: station.id,
          module_id: station.modules.find((module) => module.type === 'NAModule3').id,
          scale: 'max',
          type: 'Rain',
          optimize: false,
          date_begin: moment().subtract(1, 'day').unix().toString(),
          date_end: moment().unix().toString(),
        };
        return weathermapService.post<GetMeasureResponse>('getmeasure', params).pipe(
          map((response) => response.body),
          map((payload) => WeathermapActions.loadRainSuccess({ payload })),
        );
      }),
    );
  },
  { functional: true },
);

export const loadForecast$ = createEffect(
  (actions$ = inject(Actions), weathermapService = inject(WeathermapService)) => {
    return actions$.pipe(
      ofType(WeathermapActions.selectStation),
      filter(({ station }) => station?.modules.some((module) => module.type === 'NAModule1')),
      exhaustMap(({ station }) => {
        const params = {
          device_id: station.id,
          module_id: station.modules.find((module) => module.type === 'NAModule1').id,
        };
        return weathermapService.post<SimplifiedFutureMeasureResponse>('simplifiedfuturemeasure', params).pipe(
          map((response) => response.body),
          map((payload) => WeathermapActions.loadForecastSuccess({ payload })),
        );
      }),
    );
  },
  { functional: true },
);

export const loadStation$ = createEffect(
  (actions$ = inject(Actions), weathermapService = inject(WeathermapService)) => {
    return actions$.pipe(
      ofType(WeathermapActions.loadStation),
      exhaustMap(({ stationId }) => {
        const params = {
          device_id: stationId,
        };
        return weathermapService.post<GetPublicMeasureResponse>('getpublicmeasure', params).pipe(
          map((response) => response.body[0]),
          map((payload) => {
            const idToType = (id: string): string =>
              ({ '02:': 'NAModule1', '05:': 'NAModule3', '06:': 'NAModule2' })[id];
            const modulesId = Object.keys(payload.measures);
            const modules = modulesId.map(
              (id) =>
                ({
                  id,
                  type: idToType(id.slice(0, 3)),
                }) as Module,
            );
            return WeathermapActions.loadStationSuccess({ payload: { id: stationId, ...payload.place, modules } });
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const associateFavoriteStation$ = createEffect(
  (actions$ = inject(Actions), httpClient = inject(NgrxHttpClient)) => {
    return actions$.pipe(
      ofType(WeathermapActions.associateFavoriteStation),
      exhaustMap(({ stationId, name }) => {
        return httpClient
          .post('associatefavoritedevice', { device_id: stationId, name })
          .pipe(map(() => WeathermapActions.associateFavoriteStationSuccess({ stationId })));
      }),
    );
  },
  { functional: true },
);

export const dissociateFavoriteStation$ = createEffect(
  (actions$ = inject(Actions), httpClient = inject(NgrxHttpClient)) => {
    return actions$.pipe(
      ofType(WeathermapActions.dissociateFavoriteStation),
      exhaustMap(({ stationId }) => {
        return httpClient
          .post('dissociatefavoritedevice', { device_id: stationId })
          .pipe(map(() => WeathermapActions.dissociateFavoriteStationSuccess({ stationId })));
      }),
    );
  },
  { functional: true },
);
